import { useState } from 'react';
import { useBrand } from '../lib/brand.js';

/**
 * The fixed, canonical path for the management agency's logo. Not a config
 * value — there is deliberately no env var or database row pointing at this
 * (see brand.tsx and CLAUDE.md's white-labeling stance). It lives in
 * `apps/web/public/branding/`, which Vite copies to the built site verbatim
 * rather than fingerprinting, so swapping the logo is "replace this one file
 * with another PNG of the same name" — no code change, and on the deployed
 * VPS not even a rebuild, since the static file can be replaced directly in
 * the served `dist/`.
 *
 * `apps/api/assets/branding/company-logo.png` is a second, independent copy
 * of this same file, used to stamp a letterhead on generated PDFs
 * (lib/templated-document-pdf.ts). It has to be a duplicate rather than a
 * shared reference — the web and API images are separate deployables with no
 * shared filesystem at runtime — so swapping the logo means replacing both.
 */
const LOGO_PATH = '/branding/company-logo.png';

/**
 * The header's logo-or-name slot, shared by the staff shell, both portals,
 * the apply flow, and the public site. This is the management agency's own
 * branding (`company`), not the software's white-labelable name (`product`)
 * — see brand.tsx.
 *
 * Tries the hardcoded logo file first; falls back to the company name as
 * text if that file 404s (removed, never supplied, etc.) rather than
 * showing a broken image icon.
 */
export function BrandMark() {
  const { company } = useBrand();
  const [logoFailed, setLogoFailed] = useState(false);

  return !logoFailed ? (
    <img
      src={LOGO_PATH}
      alt={company.name}
      className="shell__brand-logo"
      onError={() => setLogoFailed(true)}
    />
  ) : (
    <strong>{company.name}</strong>
  );
}
